import { useSyncExternalStore } from 'react';
import { palettes, type Palette, type Scheme, type ThemeMode } from '@rydres/shared';

const KEY = 'rydres.theme';
const listeners = new Set<() => void>();
const media =
  typeof window !== 'undefined' && 'matchMedia' in window
    ? window.matchMedia('(prefers-color-scheme: dark)')
    : null;

function readMode(): ThemeMode {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'light' || v === 'dark' ? v : 'auto';
  } catch {
    return 'auto';
  }
}

function notify() {
  for (const l of listeners) l();
}

/** The browser's bar takes the page colour of the scheme in use, including one chosen in the picker. */
function applyThemeColor(): void {
  const color = palettes[currentScheme()].bg;
  document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]').forEach((m) => {
    m.content = color;
  });
}
let followingDevice = false;

/** Puts the saved preference on <html data-theme> so theme.css can honour it. Call before first paint. */
export function applySavedTheme(): void {
  const mode = readMode();
  const root = document.documentElement;
  if (mode === 'auto') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', mode);
  applyThemeColor();
  if (!followingDevice && media) {
    followingDevice = true;
    media.addEventListener('change', applyThemeColor);
  }
}

export function setThemeMode(mode: ThemeMode): void {
  try {
    if (mode === 'auto') localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, mode);
  } catch {
    // private window; the choice lasts for this page only
  }
  applySavedTheme();
  notify();
}

export function getThemeMode(): ThemeMode {
  return readMode();
}

export function currentScheme(): Scheme {
  const mode = readMode();
  if (mode !== 'auto') return mode;
  return media?.matches ? 'dark' : 'light';
}

function subscribe(cb: () => void) {
  listeners.add(cb);
  media?.addEventListener('change', cb);
  return () => {
    listeners.delete(cb);
    media?.removeEventListener('change', cb);
  };
}

/** The scheme in effect right now: the saved choice, or the device setting. */
export function useScheme(): Scheme {
  return useSyncExternalStore(subscribe, currentScheme, () => 'light');
}

export function useThemeMode(): ThemeMode {
  return useSyncExternalStore(subscribe, readMode, () => 'auto');
}

/** Palette values for places CSS can't reach, such as map markers. */
export function usePalette(): Palette {
  return palettes[useScheme()];
}
